import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { appendModules, pathModules, updateCollection } from '@platform/client';
import { producedRows } from '@platform/schema/generation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { LinkedModuleStory } from '../curriculum/ModuleStory';
import { SCREEN_GAP } from '../routes/PageHeader';

import { collectionChanged, collectionQuery } from './collection';
import { CollectionHeader } from './CollectionHeader';
import { CollectionSettings } from './CollectionSettings';
import { membersQuery } from './members';
import { pathChanged, placePathModules } from './path-modules';
import { PublishPanel } from './PublishPanel';
import { field } from './refusals';
import { pickerQuery } from './sources';
import { STATUS_COLOUR, archived } from './status';

import type { Collection } from './collection';
import type { CollectionLessonsResponse } from '@platform/client';

/**
 * One module, and everything done to it — GEN-60.
 *
 * `/authoring/collections/:id/publish` was the only way to reach a module: a single
 * action standing in for a home. That route now redirects here.
 *
 * Four parts, being the four questions asked of a module — what it is, which course it
 * hangs under, what it holds, and whether it can publish.
 */
export function ModuleDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const queries = useQueryClient();

  const module = useQuery(collectionQuery(id));
  const courses = useQuery(pickerQuery('COURSE'));
  const paths = useQuery(pickerQuery('PATH'));
  // Polled while an import is still writing, so lessons appear as they land.
  const members = useQuery({
    ...membersQuery(id),
    refetchInterval: (query) =>
      query.state.data?.data.generation?.live === true ? 2_000 : false,
  });

  const row = module.data?.data;
  const held = members.data?.data.lessons ?? [];
  const generation = members.data?.data.generation;
  const writing = generation?.live === true;

  const noting = generation !== undefined && noted(generation);
  // A published path the admin may not know is live, held until they confirm.
  const [confirming, setConfirming] = useState<PathOption>();
  const [addedTo, setAddedTo] = useState<string>();

  /**
   * Moving the module to another course.
   *
   * The server refuses a parent that is not a COURSE — the CHECK constraint only says a
   * module's parent is not null, so nothing stopped one being hung under a path until
   * this screen made the parent a thing a human picks.
   */
  const move = useTrackedMutation('Move the module', {
    mutationFn: async (parentId: string) => {
      await updateCollection({
        path: { id },
        body: { parentId },
        throwOnError: true,
      });
    },
    onSuccess: () => collectionChanged(queries, id),
  });

  /** The same write the Path page makes, so both pages land the same row — PATH-02. */
  const join = useTrackedMutation('Add the module to a path', {
    mutationFn: async (path: PathOption) => {
      await appendModules({
        path: { id: path.id },
        body: { moduleIds: [id] },
        throwOnError: true,
      });
      return path;
    },
    onSuccess: async (path) => {
      setConfirming(undefined);
      setAddedTo(
        path.status === 'PUBLISHED' && row?.status !== 'PUBLISHED'
          ? path.title
          : undefined,
      );
      await pathChanged(queries, path.id);
    },
  });

  const leave = useTrackedMutation('Take the module out of a path', {
    mutationFn: async (pathId: string) => {
      const { data } = await pathModules({
        path: { id: pathId },
        throwOnError: true,
      });
      await placePathModules(
        pathId,
        data.modules.map((one) => one.id).filter((other) => other !== id),
      );
      return pathId;
    },
    onSuccess: (pathId) => pathChanged(queries, pathId),
  });

  if (module.isPending) return <LinearProgress />;
  if (row === undefined)
    return <Alert severity="error">No module with that id.</Alert>;

  // An archived path is history, not somewhere to add to.
  const addable = (paths.data?.data.collections ?? []).filter(
    (one) =>
      one.status !== 'ARCHIVED' &&
      !row.paths.some((held) => held.id === one.id),
  );

  return (
    <Stack spacing={SCREEN_GAP} sx={{ maxWidth: 720 }}>
      <CollectionHeader row={row} />
      <CollectionSettings row={row} />
      <LinkedModuleStory collectionId={id} />

      <Stack spacing={1}>
        <Typography variant="h6">Course</Typography>
        {/* Not `FilterSelect`, which builds in an "Any" meaning *no filter*. This is a
            picker, and there is no such choice: a module with no course fails the publish
            gate and the database's own CHECK, so the empty option would offer a write
            that cannot land. */}
        <TextField
          select
          size="small"
          {...field('parentId')}
          value={row.parentId ?? ''}
          disabled={move.isPending}
          sx={{ maxWidth: 320 }}
          onChange={(event) => {
            move.mutate(event.target.value);
          }}
        >
          {(courses.data?.data.collections ?? []).map((one) => (
            <MenuItem key={one.id} value={one.id}>
              {one.title}
            </MenuItem>
          ))}
        </TextField>
        {row.parentId === undefined && (
          <Alert severity="warning">
            This module belongs to no course, so it cannot be published. Pick
            one.
          </Alert>
        )}
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h6">Paths</Typography>
        {row.paths.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            This module is in no path yet.
          </Typography>
        )}
        {row.paths.map((one) => (
          <Stack
            key={one.id}
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'center' }}
          >
            <Tooltip title={one.status}>
              <Badge
                variant="dot"
                color={STATUS_COLOUR[one.status]}
                aria-label={one.status}
              >
                <span />
              </Badge>
            </Tooltip>
            <Stack sx={{ flex: 1 }}>
              <Typography variant="body2">
                <Link to={`/library/paths/${one.id}`}>{one.title}</Link>
                {archived(one.status)}
              </Typography>
              {one.status === 'PUBLISHED' && (
                <Typography variant="caption" color="text.secondary">
                  Move the path back to draft to remove this module.{' '}
                  <Link to={`/library/paths/${one.id}`}>Open the path</Link>
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {String(one.position)} of {String(one.of)}
            </Typography>
            <Button
              size="small"
              color="error"
              aria-label={`Remove from ${one.title}`}
              disabled={one.status === 'PUBLISHED' || leave.isPending}
              onClick={() => {
                leave.mutate(one.id);
              }}
            >
              Remove
            </Button>
          </Stack>
        ))}
        <TextField
          select
          size="small"
          label="Add to path"
          value=""
          disabled={join.isPending}
          sx={{ maxWidth: 320 }}
          onChange={(event) => {
            const picked = addable.find((one) => one.id === event.target.value);
            if (picked === undefined) return;
            if (picked.status === 'PUBLISHED' && row.status === 'PUBLISHED')
              setConfirming(picked);
            else join.mutate(picked);
          }}
        >
          {addable.map((one) => (
            <MenuItem key={one.id} value={one.id}>
              {one.title}
            </MenuItem>
          ))}
        </TextField>
        {addedTo !== undefined && (
          <Alert
            severity="info"
            onClose={() => {
              setAddedTo(undefined);
            }}
          >
            Added to {addedTo}. Learners will see it once the module is
            published.
          </Alert>
        )}
      </Stack>

      <Dialog
        open={confirming !== undefined}
        onClose={() => {
          setConfirming(undefined);
        }}
      >
        <DialogTitle>Add to a published path?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Learners on {confirming?.title} will see this module at the end. Add
            it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirming(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={join.isPending}
            onClick={() => {
              if (confirming !== undefined) join.mutate(confirming);
            }}
          >
            Add module
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h6">Lessons</Typography>

      {members.isPending && <LinearProgress />}

      <List dense>
        {held.map((lesson) => (
          <ListItemButton
            key={lesson.id}
            component={Link}
            to={`/authoring/lessons/${lesson.id}`}
          >
            <ListItemText
              primary={`${String(lesson.position)}. ${lesson.title}`}
              secondary={`${lesson.status} · ${String(lesson.scenes)} scenes`}
            />
          </ListItemButton>
        ))}
      </List>

      {noting && <GenerationNote generation={generation} />}

      {members.isSuccess && held.length === 0 && !noting && (
        <Alert severity="info">
          This module holds no lessons yet, so there is nothing to review or
          publish.
        </Alert>
      )}

      <Typography variant="h6">Publishing</Typography>

      <PublishPanel id={id} writing={writing} />
    </Stack>
  );
}

type PathOption = Pick<Collection, 'id' | 'title' | 'status'>;

type ModuleGeneration = NonNullable<CollectionLessonsResponse['generation']>;

const unwritten = ({ lessons }: ModuleGeneration): number =>
  lessons.failed + lessons.skipped;

/** Whether the note has anything to say — and so stands in for the empty-module one. */
const noted = (generation: ModuleGeneration): boolean =>
  generation.live || unwritten(generation) > 0;

/** How far the import writing this module has got, and the way back to it. */
function GenerationNote({ generation }: { generation: ModuleGeneration }) {
  const { lessons } = generation;
  const job = (
    <Link to={`/authoring/jobs/${generation.jobId}`}>Open the import</Link>
  );

  if (generation.live)
    return (
      <Alert severity="info">
        {`${String(producedRows(lessons))} of ${String(lessons.total)} lessons written. The rest are still being written and appear here as they land. `}
        {job}
      </Alert>
    );

  return (
    <Alert severity="warning">
      {`${String(unwritten(generation))} of ${String(lessons.total)} lessons could not be written. `}
      {job} to retry them.
    </Alert>
  );
}
